import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_BASE = 'http://localhost:8000/api';

const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', Accept: 'application/json' } };
};

const HrProfileList = () => {
    const navigate = useNavigate();
    const companyId = localStorage.getItem('selectedCompanyId') || 1;

    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [originFilter, setOriginFilter] = useState('All');
    const [showModal, setShowModal] = useState(false);
    const [editingProfile, setEditingProfile] = useState(null);
    const [activeTab, setActiveTab] = useState('basic');
    const [saving, setSaving] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
    const [showArchiveConfirm, setShowArchiveConfirm] = useState(null);
    const [showInheritModal, setShowInheritModal] = useState(false);
    const [holdingProfiles, setHoldingProfiles] = useState([]);

    const emptyForm = {
        name: '', code: '', description: '', effective_date: '', shift_type: 'Day Shift',
        attendance_mode: 'Daily attendance', is_default: false, status: 'Draft',
        work_days_per_week: 5, work_hours_per_day: 8, overtime_enabled: false,
        overtime_multiplier: 1.5, max_overtime_hours: 40,
        annual_leave_days: 21, sick_leave_days: 14, unpaid_leave_allowed: true,
        leave_carry_over: false, max_carry_over_days: 5,
        base_salary_type: 'Monthly', currency: 'USD', payment_cycle: 'Monthly',
        social_security_rate: 0, tax_rate: 0, min_hours: 0, max_hours: 0,
    };
    const [formData, setFormData] = useState({ ...emptyForm });

    const handleError = (err) => {
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            navigate('/login');
            return true;
        }
        return false;
    };

    const fetchProfiles = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API_BASE}/companies/${companyId}/hr-profiles`, getAuthHeaders());
            setProfiles(res.data.data || res.data || []);
            setError(null);
        } catch (err) {
            if (!handleError(err)) setError('Failed to fetch HR profiles.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchProfiles(); }, [companyId]);

    const openCreate = () => {
        setEditingProfile(null);
        setFormData({ ...emptyForm });
        setActiveTab('basic');
        setShowModal(true);
    };

    const openEdit = (profile) => {
        setEditingProfile(profile);
        setFormData({
            name: profile.name || '', code: profile.code || '', description: profile.description || '',
            effective_date: profile.effective_date || '', shift_type: profile.shift_type || 'Day Shift',
            attendance_mode: profile.attendance_mode || 'Daily attendance',
            is_default: profile.is_default || false, status: profile.status || 'Draft',
            work_days_per_week: profile.work_days_per_week || 5,
            work_hours_per_day: profile.work_hours_per_day || 8,
            overtime_enabled: profile.overtime_enabled || false,
            overtime_multiplier: profile.overtime_multiplier || 1.5,
            max_overtime_hours: profile.max_overtime_hours || 40,
            annual_leave_days: profile.annual_leave_days || 21,
            sick_leave_days: profile.sick_leave_days || 14,
            unpaid_leave_allowed: profile.unpaid_leave_allowed !== undefined ? profile.unpaid_leave_allowed : true,
            leave_carry_over: profile.leave_carry_over || false,
            max_carry_over_days: profile.max_carry_over_days || 5,
            base_salary_type: profile.base_salary_type || 'Monthly',
            currency: profile.currency || 'USD',
            payment_cycle: profile.payment_cycle || 'Monthly',
            social_security_rate: profile.social_security_rate || 0,
            tax_rate: profile.tax_rate || 0,
            min_hours: profile.min_hours || 0, max_hours: profile.max_hours || 0,
        });
        setActiveTab('basic');
        setShowModal(true);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            if (editingProfile) {
                await axios.put(`${API_BASE}/companies/${companyId}/hr-profiles/${editingProfile.id}`, formData, getAuthHeaders());
            } else {
                await axios.post(`${API_BASE}/companies/${companyId}/hr-profiles`, formData, getAuthHeaders());
            }
            setShowModal(false);
            fetchProfiles();
        } catch (err) {
            if (!handleError(err)) alert(err.response?.data?.message || 'Error saving profile.');
        } finally {
            setSaving(false);
        }
    };

    const handleArchive = async (profile) => {
        try {
            await axios.post(`${API_BASE}/companies/${companyId}/hr-profiles/${profile.id}/archive`, {}, getAuthHeaders());
            setShowArchiveConfirm(null);
            fetchProfiles();
        } catch (err) {
            if (!handleError(err)) alert(err.response?.data?.message || 'Error archiving profile.');
        }
    };

    const handleDelete = async (profile) => {
        try {
            await axios.delete(`${API_BASE}/companies/${companyId}/hr-profiles/${profile.id}`, getAuthHeaders());
            setShowDeleteConfirm(null);
            fetchProfiles();
        } catch (err) {
            if (!handleError(err)) alert(err.response?.data?.message || 'Error deleting profile.');
        }
    };

    const handleInherit = async (holdingProfileId) => {
        try {
            await axios.post(`${API_BASE}/companies/${companyId}/hr-profiles/inherit`, { holding_profile_id: holdingProfileId }, getAuthHeaders());
            setShowInheritModal(false);
            fetchProfiles();
        } catch (err) {
            if (!handleError(err)) alert(err.response?.data?.message || 'Error inheriting profile.');
        }
    };

    const openInheritModal = async () => {
        try {
            const res = await axios.get(`${API_BASE}/companies/${companyId}/hr-profiles/available-from-holding`, getAuthHeaders());
            setHoldingProfiles(res.data.data || res.data || []);
            setShowInheritModal(true);
        } catch (err) {
            if (!handleError(err)) alert('Could not load holding profiles.');
        }
    };

    const updateForm = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

    // Filters
    const filtered = profiles.filter(p => {
        if (statusFilter !== 'All' && p.status !== statusFilter) return false;
        if (originFilter === 'Own' && p.origin === 'inherited') return false;
        if (originFilter === 'Inherited' && p.origin !== 'inherited') return false;
        if (searchTerm) {
            const s = searchTerm.toLowerCase();
            return (p.name || '').toLowerCase().includes(s) || (p.code || '').toLowerCase().includes(s) || (p.description || '').toLowerCase().includes(s);
        }
        return true;
    });

    // Summary counts
    const total = profiles.length;
    const active = profiles.filter(p => p.status === 'Active').length;
    const draft = profiles.filter(p => p.status === 'Draft').length;
    const archived = profiles.filter(p => p.status === 'Archived').length;
    const inherited = profiles.filter(p => p.origin === 'inherited').length;

    // ── Styles ──
    const pageStyle = { padding: '24px 32px', backgroundColor: '#f1f5f9', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' };
    const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' };
    const titleStyle = { fontSize: '1.5rem', fontWeight: '700', color: '#0f172a', margin: 0 };
    const btnPrimary = { padding: '10px 20px', backgroundColor: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' };
    const btnSecondary = { padding: '10px 20px', backgroundColor: '#0f172a', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem', marginRight: '8px' };
    const btnDanger = { padding: '6px 14px', backgroundColor: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.75rem' };
    const btnWarn = { padding: '6px 14px', backgroundColor: '#fef3c7', color: '#92400e', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.75rem' };
    const btnEdit = { padding: '6px 14px', backgroundColor: '#dbeafe', color: '#1d4ed8', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.75rem' };

    const cardsRow = { display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' };
    const card = (color) => ({ flex: '1', minWidth: '150px', backgroundColor: '#fff', borderRadius: '12px', padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', borderLeft: `4px solid ${color}` });
    const cardLabel = { fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '600', marginBottom: '4px' };
    const cardValue = { fontSize: '1.5rem', fontWeight: '700', color: '#0f172a' };

    const filterRow = { display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' };
    const pill = (isActive) => ({ padding: '6px 16px', borderRadius: '999px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem', backgroundColor: isActive ? '#2563eb' : '#e2e8f0', color: isActive ? '#fff' : '#475569', transition: 'all 0.15s' });

    const searchBox = { padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.875rem', width: '280px', outline: 'none' };
    const tableWrap = { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' };
    const th = { padding: '12px 16px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0', backgroundColor: '#f8fafc' };
    const td = { padding: '12px 16px', fontSize: '0.875rem', color: '#334155', borderBottom: '1px solid #f1f5f9' };

    const statusBadge = (status) => {
        const map = { Draft: { bg: '#fef3c7', color: '#92400e' }, Active: { bg: '#dcfce7', color: '#166534' }, Inactive: { bg: '#e2e8f0', color: '#475569' }, Archived: { bg: '#f1f5f9', color: '#64748b' } };
        const s = map[status] || map.Draft;
        return { display: 'inline-block', padding: '2px 10px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: s.bg, color: s.color };
    };

    const defaultBadge = { display: 'inline-block', padding: '2px 10px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: '#dcfce7', color: '#166534', marginLeft: '6px' };
    const inheritedBadge = { display: 'inline-block', padding: '2px 10px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: '#e0e7ff', color: '#3730a3', marginLeft: '6px' };

    const overlay = { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 };
    const modalBox = { backgroundColor: '#fff', borderRadius: '12px', width: '720px', maxHeight: '85vh', overflowY: 'auto', padding: '0' };
    const modalHeader = { padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
    const modalBody = { padding: '24px' };
    const modalFooter = { padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '8px' };
    const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' };
    const selectStyle = { ...inputStyle, backgroundColor: '#fff' };
    const labelStyle = { display: 'block', fontSize: '0.75rem', fontWeight: '600', color: '#374151', marginBottom: '4px' };
    const fieldGroup = { marginBottom: '16px' };
    const gridTwo = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' };
    const gridThree = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' };

    const tabRow = { display: 'flex', gap: '0', borderBottom: '2px solid #e2e8f0', marginBottom: '20px' };
    const tab = (isActive) => ({ padding: '10px 20px', border: 'none', borderBottom: isActive ? '2px solid #2563eb' : '2px solid transparent', marginBottom: '-2px', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem', backgroundColor: 'transparent', color: isActive ? '#2563eb' : '#64748b' });

    if (initialLoading) return <div style={pageStyle}><p style={{ textAlign: 'center', color: '#64748b', marginTop: '60px' }}>Loading HR profiles...</p></div>;
    if (error) return <div style={pageStyle}><p style={{ textAlign: 'center', color: '#dc2626', marginTop: '60px' }}>{error}</p></div>;

    return (
        <div style={pageStyle}>
            {/* Header */}
            <div style={headerStyle}>
                <h1 style={titleStyle}>HR Settings Profiles</h1>
                <div>
                    <button style={btnSecondary} onClick={openInheritModal}>📥 Inherit from Holding</button>
                    <button style={btnPrimary} onClick={openCreate}>+ Create New Profile</button>
                </div>
            </div>

            {/* Summary Cards */}
            <div style={cardsRow}>
                <div style={card('#2563eb')}><div style={cardLabel}>Total</div><div style={cardValue}>{total}</div></div>
                <div style={card('#16a34a')}><div style={cardLabel}>Active</div><div style={cardValue}>{active}</div></div>
                <div style={card('#d97706')}><div style={cardLabel}>Draft</div><div style={cardValue}>{draft}</div></div>
                <div style={card('#64748b')}><div style={cardLabel}>Archived</div><div style={cardValue}>{archived}</div></div>
                <div style={card('#7c3aed')}><div style={cardLabel}>Inherited</div><div style={cardValue}>{inherited}</div></div>
            </div>

            {/* Filters */}
            <div style={filterRow}>
                <button style={pill(statusFilter === 'All')} onClick={() => setStatusFilter('All')}>All</button>
                <button style={pill(statusFilter === 'Active')} onClick={() => setStatusFilter('Active')}>Active</button>
                <button style={pill(statusFilter === 'Draft')} onClick={() => setStatusFilter('Draft')}>Draft</button>
                <button style={pill(statusFilter === 'Inactive')} onClick={() => setStatusFilter('Inactive')}>Inactive</button>
                <button style={pill(statusFilter === 'Archived')} onClick={() => setStatusFilter('Archived')}>Archived</button>
                <div style={{ width: '1px', height: '24px', backgroundColor: '#e2e8f0', margin: '0 4px' }} />
                <button style={pill(originFilter === 'All')} onClick={() => setOriginFilter('All')}>All Origins</button>
                <button style={pill(originFilter === 'Own')} onClick={() => setOriginFilter('Own')}>Own</button>
                <button style={pill(originFilter === 'Inherited')} onClick={() => setOriginFilter('Inherited')}>Inherited</button>
                <div style={{ flex: 1 }} />
                <input style={searchBox} placeholder="Search by name, code..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            </div>

            {/* Table */}
            <div style={tableWrap}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={th}>Code</th>
                                <th style={th}>Name</th>
                                <th style={th}>Shift Type</th>
                                <th style={th}>Attendance</th>
                                <th style={th}>Effective Date</th>
                                <th style={th}>Status</th>
                                <th style={th}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr><td colSpan="7" style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: '40px' }}>No profiles found.</td></tr>
                            ) : filtered.map(p => (
                                <tr key={p.id} style={{ cursor: 'pointer' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'} onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}>
                                    <td style={{ ...td, fontWeight: '600', color: '#0f172a' }}>{p.code}</td>
                                    <td style={td}>
                                        <span style={{ fontWeight: '500' }}>{p.name}</span>
                                        {p.is_default && <span style={defaultBadge}>Default</span>}
                                        {p.origin === 'inherited' && <span style={inheritedBadge}>Inherited</span>}
                                        {p.description && <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '2px' }}>{p.description}</div>}
                                    </td>
                                    <td style={td}>{p.shift_type}</td>
                                    <td style={td}>{p.attendance_mode}</td>
                                    <td style={td}>{p.effective_date || '—'}</td>
                                    <td style={td}><span style={statusBadge(p.status)}>{p.status}</span></td>
                                    <td style={td}>
                                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                            <button style={btnEdit} onClick={(e) => { e.stopPropagation(); openEdit(p); }}>Edit</button>
                                            {p.status !== 'Archived' && (
                                                <button style={btnWarn} onClick={(e) => { e.stopPropagation(); setShowArchiveConfirm(p); }}>Archive</button>
                                            )}
                                            {p.status === 'Draft' && (
                                                <button style={btnDanger} onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(p); }}>Delete</button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Create / Edit Modal */}
            {showModal && (
                <div style={overlay} onClick={() => setShowModal(false)}>
                    <div style={modalBox} onClick={e => e.stopPropagation()}>
                        <div style={modalHeader}>
                            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: '#0f172a' }}>
                                {editingProfile ? 'Edit Profile' : 'Create New Profile'}
                            </h2>
                            <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#94a3b8' }}>✕</button>
                        </div>

                        {/* Tabs */}
                        <div style={{ padding: '0 24px' }}>
                            <div style={tabRow}>
                                <button style={tab(activeTab === 'basic')} onClick={() => setActiveTab('basic')}>Basic Info</button>
                                <button style={tab(activeTab === 'schedule')} onClick={() => setActiveTab('schedule')}>Work Schedule</button>
                                <button style={tab(activeTab === 'leave')} onClick={() => setActiveTab('leave')}>Leave Settings</button>
                                <button style={tab(activeTab === 'payroll')} onClick={() => setActiveTab('payroll')}>Payroll</button>
                            </div>
                        </div>

                        <div style={modalBody}>
                            {/* Basic Info */}
                            {activeTab === 'basic' && (
                                <>
                                    <div style={gridTwo}>
                                        <div style={fieldGroup}>
                                            <label style={labelStyle}>Profile Name *</label>
                                            <input style={inputStyle} value={formData.name} onChange={e => updateForm('name', e.target.value)} placeholder="e.g. Standard Office" />
                                        </div>
                                        <div style={fieldGroup}>
                                            <label style={labelStyle}>Profile Code *</label>
                                            <input style={inputStyle} value={formData.code} onChange={e => updateForm('code', e.target.value)} placeholder="e.g. HR-STD-01" />
                                        </div>
                                    </div>
                                    <div style={fieldGroup}>
                                        <label style={labelStyle}>Description</label>
                                        <textarea style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} value={formData.description} onChange={e => updateForm('description', e.target.value)} placeholder="Optional description..." />
                                    </div>
                                    <div style={gridThree}>
                                        <div style={fieldGroup}>
                                            <label style={labelStyle}>Effective Date *</label>
                                            <input style={inputStyle} type="date" value={formData.effective_date} onChange={e => updateForm('effective_date', e.target.value)} />
                                        </div>
                                        <div style={fieldGroup}>
                                            <label style={labelStyle}>Shift Type *</label>
                                            <select style={selectStyle} value={formData.shift_type} onChange={e => updateForm('shift_type', e.target.value)}>
                                                <option>Day Shift</option><option>Night Shift</option><option>Flexible Shift</option>
                                            </select>
                                        </div>
                                        <div style={fieldGroup}>
                                            <label style={labelStyle}>Attendance Mode *</label>
                                            <select style={selectStyle} value={formData.attendance_mode} onChange={e => updateForm('attendance_mode', e.target.value)}>
                                                <option>Daily attendance</option><option>Hourly attendance</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div style={gridTwo}>
                                        <div style={fieldGroup}>
                                            <label style={labelStyle}>Status</label>
                                            <select style={selectStyle} value={formData.status} onChange={e => updateForm('status', e.target.value)}>
                                                <option>Draft</option><option>Active</option><option>Inactive</option><option>Archived</option>
                                            </select>
                                        </div>
                                        <div style={{ ...fieldGroup, display: 'flex', alignItems: 'center', paddingTop: '22px' }}>
                                            <input type="checkbox" checked={formData.is_default} onChange={e => updateForm('is_default', e.target.checked)} style={{ marginRight: '8px' }} />
                                            <label style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>Set as Default Profile</label>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Work Schedule */}
                            {activeTab === 'schedule' && (
                                <>
                                    <div style={gridTwo}>
                                        <div style={fieldGroup}>
                                            <label style={labelStyle}>Work Days per Week</label>
                                            <input style={inputStyle} type="number" min="1" max="7" value={formData.work_days_per_week} onChange={e => updateForm('work_days_per_week', parseInt(e.target.value) || 0)} />
                                        </div>
                                        <div style={fieldGroup}>
                                            <label style={labelStyle}>Work Hours per Day</label>
                                            <input style={inputStyle} type="number" min="1" max="24" value={formData.work_hours_per_day} onChange={e => updateForm('work_hours_per_day', parseInt(e.target.value) || 0)} />
                                        </div>
                                    </div>
                                    <div style={{ ...fieldGroup, display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                                        <input type="checkbox" checked={formData.overtime_enabled} onChange={e => updateForm('overtime_enabled', e.target.checked)} />
                                        <label style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>Enable Overtime</label>
                                    </div>
                                    {formData.overtime_enabled && (
                                        <div style={gridTwo}>
                                            <div style={fieldGroup}>
                                                <label style={labelStyle}>Overtime Multiplier</label>
                                                <input style={inputStyle} type="number" step="0.1" min="1" value={formData.overtime_multiplier} onChange={e => updateForm('overtime_multiplier', parseFloat(e.target.value) || 1)} />
                                            </div>
                                            <div style={fieldGroup}>
                                                <label style={labelStyle}>Max Overtime Hours / Month</label>
                                                <input style={inputStyle} type="number" min="0" value={formData.max_overtime_hours} onChange={e => updateForm('max_overtime_hours', parseInt(e.target.value) || 0)} />
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Leave Settings */}
                            {activeTab === 'leave' && (
                                <>
                                    <div style={gridTwo}>
                                        <div style={fieldGroup}>
                                            <label style={labelStyle}>Annual Leave Days</label>
                                            <input style={inputStyle} type="number" min="0" value={formData.annual_leave_days} onChange={e => updateForm('annual_leave_days', parseInt(e.target.value) || 0)} />
                                        </div>
                                        <div style={fieldGroup}>
                                            <label style={labelStyle}>Sick Leave Days</label>
                                            <input style={inputStyle} type="number" min="0" value={formData.sick_leave_days} onChange={e => updateForm('sick_leave_days', parseInt(e.target.value) || 0)} />
                                        </div>
                                    </div>
                                    <div style={{ ...fieldGroup, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input type="checkbox" checked={formData.unpaid_leave_allowed} onChange={e => updateForm('unpaid_leave_allowed', e.target.checked)} />
                                        <label style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>Allow Unpaid Leave</label>
                                    </div>
                                    <div style={{ ...fieldGroup, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input type="checkbox" checked={formData.leave_carry_over} onChange={e => updateForm('leave_carry_over', e.target.checked)} />
                                        <label style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>Allow Leave Carry Over</label>
                                    </div>
                                    {formData.leave_carry_over && (
                                        <div style={{ ...fieldGroup, maxWidth: '50%' }}>
                                            <label style={labelStyle}>Max Carry Over Days</label>
                                            <input style={inputStyle} type="number" min="0" value={formData.max_carry_over_days} onChange={e => updateForm('max_carry_over_days', parseInt(e.target.value) || 0)} />
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Payroll Settings */}
                            {activeTab === 'payroll' && (
                                <>
                                    <div style={gridThree}>
                                        <div style={fieldGroup}>
                                            <label style={labelStyle}>Salary Type</label>
                                            <select style={selectStyle} value={formData.base_salary_type} onChange={e => updateForm('base_salary_type', e.target.value)}>
                                                <option>Monthly</option><option>Hourly</option>
                                            </select>
                                        </div>
                                        <div style={fieldGroup}>
                                            <label style={labelStyle}>Currency</label>
                                            <input style={inputStyle} value={formData.currency} onChange={e => updateForm('currency', e.target.value)} placeholder="USD" />
                                        </div>
                                        <div style={fieldGroup}>
                                            <label style={labelStyle}>Payment Cycle</label>
                                            <select style={selectStyle} value={formData.payment_cycle} onChange={e => updateForm('payment_cycle', e.target.value)}>
                                                <option>Monthly</option><option>Bi-weekly</option><option>Weekly</option>
                                            </select>
                                        </div>
                                    </div>
                                    {formData.base_salary_type === 'Monthly' && (
                                        <div style={gridTwo}>
                                            <div style={fieldGroup}>
                                                <label style={labelStyle}>Social Security Rate (%)</label>
                                                <input style={inputStyle} type="number" step="0.01" min="0" max="100" value={formData.social_security_rate} onChange={e => updateForm('social_security_rate', parseFloat(e.target.value) || 0)} />
                                            </div>
                                            <div style={fieldGroup}>
                                                <label style={labelStyle}>Tax Rate (%)</label>
                                                <input style={inputStyle} type="number" step="0.01" min="0" max="100" value={formData.tax_rate} onChange={e => updateForm('tax_rate', parseFloat(e.target.value) || 0)} />
                                            </div>
                                        </div>
                                    )}
                                    {formData.base_salary_type === 'Hourly' && (
                                        <div style={gridTwo}>
                                            <div style={fieldGroup}>
                                                <label style={labelStyle}>Minimum Hours</label>
                                                <input style={inputStyle} type="number" min="0" value={formData.min_hours} onChange={e => updateForm('min_hours', parseInt(e.target.value) || 0)} />
                                            </div>
                                            <div style={fieldGroup}>
                                                <label style={labelStyle}>Maximum Hours</label>
                                                <input style={inputStyle} type="number" min="0" value={formData.max_hours} onChange={e => updateForm('max_hours', parseInt(e.target.value) || 0)} />
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div style={modalFooter}>
                            <button onClick={() => setShowModal(false)} style={{ ...btnPrimary, backgroundColor: '#e2e8f0', color: '#475569' }}>Cancel</button>
                            <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
                                {saving ? 'Saving...' : (editingProfile ? 'Update Profile' : 'Create Profile')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Inherit from Holding Modal */}
            {showInheritModal && (
                <div style={overlay} onClick={() => setShowInheritModal(false)}>
                    <div style={{ ...modalBox, width: '520px' }} onClick={e => e.stopPropagation()}>
                        <div style={modalHeader}>
                            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: '#0f172a' }}>Inherit from Holding</h2>
                            <button onClick={() => setShowInheritModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#94a3b8' }}>✕</button>
                        </div>
                        <div style={modalBody}>
                            {holdingProfiles.length === 0 ? (
                                <p style={{ textAlign: 'center', color: '#94a3b8' }}>No holding profiles available to inherit.</p>
                            ) : holdingProfiles.map(hp => (
                                <div key={hp.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #f1f5f9' }}>
                                    <div>
                                        <div style={{ fontWeight: '600', color: '#0f172a' }}>{hp.name} <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>({hp.code})</span></div>
                                        <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{hp.shift_type} · {hp.attendance_mode}</div>
                                    </div>
                                    <button style={btnPrimary} onClick={() => handleInherit(hp.id)}>Inherit</button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Archive Confirm */}
            {showArchiveConfirm && (
                <div style={overlay} onClick={() => setShowArchiveConfirm(null)}>
                    <div style={{ ...modalBox, width: '420px' }} onClick={e => e.stopPropagation()}>
                        <div style={modalHeader}>
                            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#0f172a' }}>Archive Profile</h2>
                        </div>
                        <div style={modalBody}>
                            <p style={{ color: '#475569', margin: 0 }}>Archive <strong>"{showArchiveConfirm.name}"</strong>? This profile will no longer be assignable to new employees or departments.</p>
                        </div>
                        <div style={modalFooter}>
                            <button onClick={() => setShowArchiveConfirm(null)} style={{ ...btnPrimary, backgroundColor: '#e2e8f0', color: '#475569' }}>Cancel</button>
                            <button onClick={() => handleArchive(showArchiveConfirm)} style={{ ...btnPrimary, backgroundColor: '#d97706' }}>Archive</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirm */}
            {showDeleteConfirm && (
                <div style={overlay} onClick={() => setShowDeleteConfirm(null)}>
                    <div style={{ ...modalBox, width: '420px' }} onClick={e => e.stopPropagation()}>
                        <div style={modalHeader}>
                            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#dc2626' }}>Delete Profile</h2>
                        </div>
                        <div style={modalBody}>
                            <p style={{ color: '#475569', margin: 0 }}>Permanently delete <strong>"{showDeleteConfirm.name}"</strong>? This cannot be undone. Only Draft profiles with no assignments can be deleted.</p>
                        </div>
                        <div style={modalFooter}>
                            <button onClick={() => setShowDeleteConfirm(null)} style={{ ...btnPrimary, backgroundColor: '#e2e8f0', color: '#475569' }}>Cancel</button>
                            <button onClick={() => handleDelete(showDeleteConfirm)} style={{ ...btnPrimary, backgroundColor: '#dc2626' }}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HrProfileList;